import { useEffect, useMemo, useState } from 'react';
import { acquireColorImage } from './api/colorImageApi';

const EMPTY = [];
// Call only with colors actually displayed by this mounted viewer.
export function useColorImages(colors = EMPTY, enabled = true) {
  const source = colors || EMPTY;
  const key = JSON.stringify(enabled ? Array.from(new Map(source
    .filter(c => c?.colorId != null && String(c.colorId).trim() !== '' &&
      Number.isSafeInteger(Number(c.colorId)) && Number(c.colorId) >= 0)
    .map(c => [String(c.colorId), [String(c.colorId), c.lastUpdateDate || '']])).values()) : []);
  const [result, setResult] = useState({ key: null, images: {}, errors: {} });
  useEffect(() => {
    let active = true;
    setResult({ key, images: {}, errors: {} });
    const requests = JSON.parse(key).map(([id, version]) => {
      const request = acquireColorImage(id, version);
      return { ...request, id };
    });
    // Update a 3D scene once per set, rather than rebuilding it for each image.
    Promise.all(requests.map(async request => {
      try { return { id: request.id, image: await request.promise }; }
      catch (error) { return { id: request.id, error: error.message }; }
    })).then(loaded => {
      if (!active) return;
      const images = {}, errors = {};
      loaded.forEach(item => {
        if (item.error) errors[item.id] = item.error;
        else images[item.id] = item.image;
      });
      setResult({ key, images, errors });
    });
    return () => { active = false; requests.forEach(request => request.release()); };
  }, [key]);
  return useMemo(() => source.map(color => ({
    ...color,
    imageData: result.key === key ? result.images[String(color.colorId)] : undefined,
    imageError: result.key === key ? result.errors[String(color.colorId)] : undefined
  })), [source, result, key]);
}

export function useColorImage(color, enabled = true) {
  const colors = useMemo(() => color ? [color] : EMPTY, [color]);
  return useColorImages(colors, enabled)[0];
}
